import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useAppStore } from '@/stores/app'
import { useFetchStore } from '@/stores/fetch'
import type { IPendingScreen } from '@/types/Screen'

/*
 * DISPLAYS WAITING TO BE CONFIRMED in the tenant being worked in ("Čakajo na potrditev",
 * docs/API.md → Pending screens). One list for the whole console, so Zasloni, Pregled and the
 * menu's badge show the same number: the tenant's own pending displays (GET
 * /tenants/{tid}/pairings, admins only — with the displays without a tenant on the network
 * of one of its admins, `matched_by: 'network'`) and — for a provider admin — every display
 * that named no tenant (GET /pairings, `tenant_id: null`), which only the provider can place.
 *
 * `load` is called by `usePendingPoll` (libs/pendingScreens.ts) while a page that shows the
 * list is visible. A failed poll keeps the last answer: the next one tries again.
 */
export const usePendingStore = defineStore('pending', () => {
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()

    const tid   = ref('')
    const items = ref<IPendingScreen[]>([])

    const count = computed((): number => items.value.length)

    /** Newest first, as the API answers each list. */
    const newestFirst = (a: IPendingScreen, b: IPendingScreen): number => Date.parse(b.created_at) - Date.parse(a.created_at)

    const load = async (forTid: string) => {
        // No tenant yet (the menu before GET /me), or not an admin there: nothing to ask.
        if (!forTid || !appStore.isAdmin(forTid)) {
            if (tid.value === forTid) items.value = []

            return
        }

        if (tid.value !== forTid) {
            tid.value   = forTid
            items.value = []
        }

        const provider = appStore.isProviderAdmin()

        const [own, all] = await Promise.all([
            fetchStore.fetch(`/tenants/${forTid}/pairings`).get().json<IPendingScreen[]>(),
            provider ? fetchStore.fetch('/pairings').get().json<IPendingScreen[]>() : Promise.resolve(null),
        ])

        // The tenant changed while the answers were on their way.
        if (tid.value !== forTid || own.statusCode.value !== 200) return

        const mine = own.data.value ?? []

        // A display without a tenant on this tenant's network is in both answers: the
        // tenant's copy (`matched_by: 'network'`) is kept.
        const listed = new Set(mine.map((item) => item.id))

        const withoutTenant = all && all.statusCode.value === 200
            ? (all.data.value ?? []).filter((item) => item.tenant_id === null && !listed.has(item.id))
            : []

        items.value = [...mine, ...withoutTenant].sort(newestFirst)
    }

    /** Drops an entry at once (confirmed or turned away); the next poll agrees. */
    const drop = (id: string) => {
        items.value = items.value.filter((item) => item.id !== id)
    }

    return { tid, items, count, load, drop }
})
